import React, { useState } from "react";

import { Tooltip } from "@material-ui/core";
import { Button, useNotify, useRefresh } from "react-admin";

import { ConditionalConfirm } from "../ConditionalConfirm";
import { LinkOffIcon } from "../iconography";
import { logError } from "../../../utils/appUtils";
import { OutboundStatus } from "../../../utils/constants";
import { isUserFacingError } from "../../../utils/lambdaErrors";
import {
  clearShipWithMemberCache,
  isLinked,
  unlinkOutbound,
} from "../../../utils/shipWith";
import useShipWithMembers from "../../../utils/useShipWithMembers";

export const CHECK_IN_BLOCKER =
  "Cancel the truck's gate check-in before unlinking";
export const PAST_PENDING_BLOCKER = "Only pending loads can be unlinked";

/** Once the truck is on the yard the group is the check-in's, not the form's. */
const PAST_PENDING = [
  OutboundStatus.ARRIVED,
  OutboundStatus.PLACED,
  OutboundStatus.LOADED,
  OutboundStatus.RELEASED,
];

/**
 * Why the load cannot leave its group, or null when it can. The check-in
 * comes first because cancelling it is what makes the unlink possible; a load
 * that is simply past PENDING has no way back.
 */
export const getUnlinkBlocker = (record, members = []) => {
  if (record?.gateCheckInId || members.some((member) => member?.gateCheckInId))
    return CHECK_IN_BLOCKER;
  if (PAST_PENDING.includes(record?.status)) return PAST_PENDING_BLOCKER;
  return null;
};

export const getUnlinkMessage = (record) =>
  `Remove PO ${record?.poNumber ?? ""} from its ships-together group? It will need its own pickup appointment.`;

/**
 * UNLINK, for the edit form's action toolbar (manager OutboundEditActions and
 * customer EditActions — AD-8). Rendered only for a linked load. The lambda
 * clears the leaving load's appointment (AD-7); a group that shrinks to one
 * member dissolves server-side.
 */
export default function UnlinkButton({ record, disabled = false }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const { members } = useShipWithMembers(record);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!isLinked(record)) return null;

  const blocker = getUnlinkBlocker(record, members);

  const handleClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setOpen(true);
  };

  const handleConfirm = async () => {
    setOpen(false);
    setSaving(true);
    try {
      await unlinkOutbound(record.id, { reloadYardId: record.reloadYardId });
      // The badge tooltips of the remaining members would otherwise list this
      // load for up to a minute.
      clearShipWithMemberCache(record.shipWithIds);
      notify("Load unlinked");
      refresh();
    } catch (error) {
      if (isUserFacingError(error)) notify(error.message, "warning");
      else logError("shipWith:UnlinkButton:unlink", error, notify);
    } finally {
      setSaving(false);
    }
  };

  const button = (
    <Button
      label="Unlink"
      color="primary"
      disabled={disabled || Boolean(blocker) || saving}
      onClick={handleClick}
    >
      <LinkOffIcon />
    </Button>
  );

  return (
    <>
      {blocker ? (
        // A disabled button fires no pointer events, so the tooltip listens
        // on a wrapper.
        <Tooltip title={blocker} arrow>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
      <ConditionalConfirm
        isOpen={open}
        loading={saving}
        title="Unlink"
        content={getUnlinkMessage(record)}
        confirm="Unlink"
        confirmColor="warning"
        onConfirm={handleConfirm}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
